import { getAuthHeaders } from "./authSession";

export type RingDevice = {
  id: number;
  kind: "doorbell" | "camera" | "chime";
  name: string;
  model: string;
  battery: number | null;
  firmware: string | null;
  wifiSignal: number | null;
};

export type RingStatus = {
  connected: boolean;
  devices: RingDevice[];
  fetchedAt?: number;
};

export type RingLoginResult =
  | (RingStatus & { needsCode?: false })
  | { needsCode: true; method: string | null; sentTo: string | null };

const post = async <T>(payload: object): Promise<T> => {
  const res = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    let message = text || `Request failed (${res.status})`;
    try {
      message = JSON.parse(text).error ?? message;
    } catch {}
    throw new Error(message);
  }

  return (await res.json()) as T;
};

export const getRingStatus = () => post<RingStatus>({ action: "getRingStatus" });

/** Step one of signing in. Ring may reply that it has sent a code. */
export const ringSignIn = (email: string, password: string) =>
  post<RingLoginResult>({ action: "ringLogin", email, password });

/** Step two: the code Ring sent, finishing the sign-in from step one. */
export const ringVerifyCode = (code: string) =>
  post<RingLoginResult>({ action: "ringLogin", code });

/** Fallback: a refresh token made by another tool, such as ring-auth-cli from @tsightler/ring-client-api. */
export const saveRingRefreshToken = (refreshToken: string) =>
  post<RingStatus>({ action: "saveRingRefreshToken", refreshToken });

export const disconnectRing = () => post<{ ok: true }>({ action: "deleteRingToken" });

export type RingEvent = {
  id: string;
  deviceId: number;
  /** "ding" (doorbell pressed), "motion" or "on_demand" (live view). */
  type: string;
  at: string;
  /** Ring's own description, e.g. "A person is walking on the driveway." */
  description: string | null;
  person: boolean;
};

export type RingEvents = {
  connected: boolean;
  devices: Pick<RingDevice, "id" | "kind" | "name" | "battery">[];
  events: RingEvent[];
};

/** Devices with their batteries, and recent events newest first. */
export const getRingEvents = () =>
  post<RingEvents>({ action: "getRingEvents", noThumbnail: true });

export type LiveViewState = "connecting" | "waking" | "live" | "ended";

const RING_SIGNALLING = "wss://api.prod.signalling.ring.devices.a2z.com:443/ws";
const RING_ICE_SERVERS = [
  "stun:stun.kinesisvideo.us-east-1.amazonaws.com:443",
  "stun:stun.l.google.com:19302",
  "stun:stun1.l.google.com:19302",
].map((urls) => ({ urls }));

/**
 * Live view straight from the browser. The Lambda only issues a one-time
 * ticket; the browser opens Ring's signalling socket and runs the WebRTC call
 * itself, so the video goes directly from Ring to the hub. The codec is
 * negotiated (H.265 where the browser supports it, otherwise H.264).
 * Resolves to a function that ends the view.
 */
export async function startRingLiveView(
  deviceId: number,
  video: HTMLVideoElement,
  onState: (state: LiveViewState) => void,
): Promise<() => void> {
  const { ticket } = await post<{ ticket: string }>({ action: "getRingLiveTicket" });

  const dialogId = crypto.randomUUID();
  const pc = new RTCPeerConnection({ iceServers: RING_ICE_SERVERS });
  // Receive only: the hub doesn't talk back through the doorbell (yet).
  pc.addTransceiver("audio", { direction: "recvonly" });
  pc.addTransceiver("video", { direction: "recvonly" });
  const ws = new WebSocket(
    `${RING_SIGNALLING}?api_version=4.0&auth_type=ring_solutions&client_id=ring_site-${crypto.randomUUID()}&token=${ticket}`,
  );

  let sessionId: string | null = null;
  let offerSent = false;
  let ended = false;
  const send = (method: string, body: object) => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ method, dialog_id: dialogId, body: { doorbot_id: deviceId, ...body } }));
    }
  };

  const stop = () => {
    if (ended) return;
    ended = true;
    window.clearInterval(ping);
    send("close", { session_id: sessionId });
    ws.close();
    pc.close();
    video.srcObject = null;
  };
  const end = () => {
    if (!ended) onState("ended");
    stop();
  };
  // Ring drops a session that goes quiet.
  const ping = window.setInterval(() => sessionId && send("ping", { session_id: sessionId }), 5000);

  pc.ontrack = (e) => {
    if (e.track.kind === "video" || !video.srcObject) {
      video.srcObject = e.streams[0] ?? new MediaStream([e.track]);
    }
  };
  pc.onicecandidate = (e) => {
    if (e.candidate && offerSent) {
      send("ice", { ice: e.candidate.candidate, mlineindex: e.candidate.sdpMLineIndex });
    }
  };
  pc.onconnectionstatechange = () => {
    if (pc.connectionState === "failed" || pc.connectionState === "closed") end();
  };
  video.onplaying = () => onState("live");

  ws.onopen = async () => {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send("live_view", {
      stream_options: { audio_enabled: true, video_enabled: true },
      sdp: offer.sdp,
    });
    offerSent = true;
  };
  ws.onclose = end;
  ws.onerror = end;
  ws.onmessage = async (event) => {
    const message = JSON.parse(event.data);
    const body = message.body ?? {};
    if (body.doorbot_id !== undefined && body.doorbot_id !== deviceId) return;
    if (body.session_id && !sessionId) sessionId = body.session_id;

    switch (message.method) {
      case "sdp":
        await pc.setRemoteDescription({ type: "answer", sdp: body.sdp });
        onState("waking");
        // Without these Ring ends the stream after about 70 seconds.
        send("activate_session", { session_id: sessionId });
        send("stream_options", { session_id: sessionId, audio_enabled: true, video_enabled: true });
        break;
      case "ice":
        await pc.addIceCandidate({ candidate: body.ice, sdpMLineIndex: body.mlineindex }).catch(() => {});
        break;
      case "close":
        end();
        break;
    }
  };

  return stop;
}
